/******************************************************************************
 * Encapsulates the data and actions associated with Product Search.
 *
 * The following search types are supported:
 *   (1) Products In Design search
 *   (2) Product Alternatives search
 *   (3) Product Catalog search
 *
 * The basic action side flow is:
 *   (1) dispatch ProductSearch.createSearch(...) action
 *   (2) dispatch ProductSearch.updateFilters(...) action(s) [optional]
 *   (3) dispatch ProductSearch.runSearch(...) action
 *
 * The basic render side flow is:
 *   (1) connect to getSearchResultFor(...) to receive search results
 *   (2) connect to getAvailableFiltersFor(...) to receive all available filters
 *   (3) connect to getEnabledFiltersFor(...) to receive the current enabled filters
 *
 * IMPORTANT:
 * Searches are stored in redux state and indexed by name. There can be
 * only 1 search per name. To support multiple searches of the same type,
 * give them unique names.
 *****************************************************************************/
import { bugsnagError, bugsnagWarn } from 'services/bugsnag';

import ProductAlternatives from './ProductAlternatives';
import ProductCatalog from './ProductCatalog';
import ProductsInDesign from './ProductsInDesign';
import { FEATURE_FLAGS, getFeatureFlagEnabledBool } from 'shared/reducers/featureFlag';
import { convertDimensionsForAPI } from './utils';

/**
 * Product Search service.
 * Manages product search lifecycle.
 *
 * Controllers should import and use the Product Search service
 * for all product search requests.
 */
export default class ProductSearch {
  /**
   * The supported search types.
   */
  static SEARCH_TYPE_PRODUCTS_IN_DESIGN = 'PID';
  static SEARCH_TYPE_PRODUCT_ALTERNATIVES = 'PA';
  static SEARCH_TYPE_PRODUCT_ALTERNATIVES_CATALOG = 'PACSearch';
  static SEARCH_TYPE_PRODUCT_CATALOG = 'PC';

  // ============================
  // ACTIONS / CALLBACK DATA
  // ============================
  /**
    * Action fired whenever a search is updated.
    * @event ProductSearch#updated
    * @type {Object}
    * @property {string} type
    * @property {Search} payload
    */
   static SEARCH_UPDATED = 'PRODUCT_SEARCH/SEARCH_UPDATED';

  // ==================
  // ACTIONS / MUTATORS
  // ==================

  /**
   * Creates a new search query.
   *
   * If a search with provided name already exists then
   * it will be overwritten with a new search instance.
   *
   * param {Object} search
   * @param {string} search.uniqueName - A unique name for this search
   * @param {string} search.type - The search type
   * @param {Object} search.[...rest] - Extra params passed down to search constructors.
   */
  static createSearch = ({ uniqueName, type, ...rest }) => (dispatch, getState) => {
    let search = null;
    const isAlts = type === ProductSearch.SEARCH_TYPE_PRODUCT_ALTERNATIVES;
    const isProductInDesign = type === ProductSearch.SEARCH_TYPE_PRODUCTS_IN_DESIGN;
    const isProductCatalog = type === ProductSearch.SEARCH_TYPE_PRODUCT_CATALOG;
    const searchProps = { name: uniqueName, type, ...rest };

    switch (true) {
      case isAlts:
        search = new ProductAlternatives(searchProps);
        break;
      case isProductInDesign:
        search = new ProductsInDesign(searchProps);
        break;
      case isProductCatalog:
        search = new ProductCatalog(searchProps);
        break;
      default:
        bugsnagError('Unsupported ProductSearch type', { uniqueName, type });
        break;
    }

    if (!search) return;

    dispatch(ProductSearch._updateSearchState(search));
  }

  static resetSearch = ({
    searchKey,
    searchType,
    searchValue = '',
    selectedSizeUnit,
    numberOf2dResults,
    numberOf3dResults,
    enabledFilters,
    partnerSlug,
    productId
  }) => async (dispatch, getState) => {
    // Create new search only if searchKey does not exist yet.
    const currentSearchKeys = ProductSearch.getAllSearchKeys({ state: getState() });
    const updatedFilterUiEnabled = getFeatureFlagEnabledBool(FEATURE_FLAGS.AlternativesFilters, getState());

    if (!currentSearchKeys[searchKey]) {
      dispatch(ProductSearch.createSearch({
        uniqueName: searchKey,
        type: searchType,
        productId, // Required for alt search. In design search will ignore.
        partnerSlug, // Required for alt search. In design search will ignore.
        searchValue, // Required for alt catalog search. In design search & pure alt search will ignore
        numberOf2dResults,
        numberOf3dResults
      }));
    }

    if (searchType === ProductSearch.SEARCH_TYPE_PRODUCT_ALTERNATIVES) {
      const search = getState()[ProductSearch.reducerKey][searchKey];
      search.clearCache();
      dispatch(ProductSearch.updateProduct({
        name: searchKey,
        productId
      }));
    }

    if (enabledFilters) {
      dispatch(ProductSearch.updateFilters({
        name: searchKey,
        enabledFilters
      }));
    }

    if (!this.isRunning({name: searchKey, state: getState()})) {
      await dispatch(ProductSearch.runSearch({
        selectedSizeUnit,
        updatedFilterUiEnabled,
        name: searchKey
      }));
    }

    return Promise.resolve();
  }

  /**
   * Runs the search resulting in an updated result.
   * If the search was run previously, the old response
   * will be overwritten with the new result.
   * @param {string} name - The unique search name
   */
  static runSearch = ({ name, ...rest }) => async (dispatch, getState) => {
    // Accessing search data via a function here so that we are
    // always using the latest updated search from redux state.
    const search = () => getState()[ProductSearch.reducerKey][name];
    const updatedFilterUiEnabled = getFeatureFlagEnabledBool(FEATURE_FLAGS.AlternativesFilters, getState());
    const selectedSizeUnit = rest.selectedSizeUnit;
    const overrideDefaultProductType = rest.overrideDefaultProductType;
    const enabledFilters = search().enabledFilters();

    if (!search()) {
      bugsnagWarn('Attempted to run a non-existing search', { name });
      return;
    }

    // Check cache.
    if (search().hasCaching()) {
      const { hit, searchResults } = search().checkCache(rest);
      if (hit && searchResults) {
        // Perf Optimization: if cached results are already the
        // latest search results, then elide the update.
        if (searchResults !== search().searchResults()) {
          // Update state with the search results.
          let updates = { searchResults };
          if (searchResults.enabledFilters) {
            updates.enabledFilters = searchResults.enabledFilters;
          }
          dispatch(ProductSearch._updateSearchState(search().makeCopy(updates)));
        }
        return Promise.resolve();
      }
    }

    // We need to convert the stored dimensions to mm before sending to the BE.
    // I'm not sure if there's a better way to handle this.
    if (updatedFilterUiEnabled && Object.keys(enabledFilters).length) {
      const [Width, Height, Depth] = convertDimensionsForAPI({ enabledFilters, selectedSizeUnit });

      await dispatch(ProductSearch.updateFilters({
        name,
        enabledFilters: {
          ...enabledFilters,
          Width,
          Height,
          Depth
        }
      }));
    }

    // For asynchronous searches, we need to set
    // the search status to running. We should also
    // clear any errors when a new search begins.
    let preSearchUpdates = { error: null };
    if (search().isAsync()) {
      preSearchUpdates.status = 'Running';
    }
    dispatch(ProductSearch._updateSearchState(
      search().makeCopy(preSearchUpdates)
    ));

    // Execute the search, handle responses and errors.
    try {
      const searchResults = await search().runQuery({
        overrideDefaultProductType,
        selectedSizeUnit,
        updatedFilterUiEnabled,
        ...rest
      });

      let updates = { status: 'Complete' };
      if (searchResults) {
        updates.searchResults = searchResults;

        if (searchResults.enabledFilters) {
          updates.enabledFilters = searchResults.enabledFilters;
        }

        if (searchResults.enabledSort) {
          updates.enabledSort = searchResults.enabledSort;
        }
      }

      dispatch(ProductSearch._updateSearchState(search().makeCopy(updates)));
      return searchResults;
    } catch (error) {
      const errorInfo = { type: 'requestError', response: 'Search failed' };
      dispatch(ProductSearch._updateSearchState(
        search().makeCopy({ status: 'Error', error: errorInfo })
      ));

      bugsnagError(error, { search: search() });
    }

    return Promise.resolve();
  }

  /**
   * For paginated searches, loads the next page of results.
   * Results are appended to the existing search results.
   * @param {string} name - The unique search name
   */
  static loadNextPage = ({ name }) => async (dispatch, getState) => {
    // Accessing search data via a function here so that we are
    // always using the latest updated search from redux state.
    const search = () => getState()[ProductSearch.reducerKey][name];

    if (!search()) {
      bugsnagWarn('Attempted to load next page for a non-existing search', { name });
      return;
    }

    // Loading next page does not apply to all searches.
    if (!search().hasPagination() || search().isRunning()) {
      return Promise.resolve();
    }

    // Update the status.
    dispatch(ProductSearch._updateSearchState(
      search().makeCopy({ status: 'LoadingNextPage' })
    ));

    // Execute the search, handle responses and errors.
    let searchResults = null;
    try {
      searchResults = await search().loadNextPage({ ...getState() });
    } catch (error) {
      bugsnagError(error, { search: search() });
    }

    let updates = { status: 'Complete' };
    if (searchResults) {
      updates.searchResults = searchResults;
    }

    dispatch(ProductSearch._updateSearchState(search().makeCopy(updates)));

    return Promise.resolve();
  }

  /**
   * For paginated searches, loads the previous page of results.
   * @param {string} name - The unique search name
   */
  static loadPreviousPage = ({ name }) => async (dispatch, getState) => {
    // Accessing search data via a function here so that we are
    // always using the latest updated search from redux state.
    const search = () => getState()[ProductSearch.reducerKey][name];

    if (!search()) {
      bugsnagWarn('Attempted to load next page for a non-existing search', { name });
      return;
    }

    // Loading previous page does not apply to all searches.
    if (!search().hasPagination()) {
      return Promise.resolve();
    }

    // Update the status.
    dispatch(ProductSearch._updateSearchState(
      search().makeCopy({ status: 'LoadingPreviousPage' })
    ));

    // Execute the search, handle responses and errors.
    let searchResults = null;
    try {
      searchResults = await search().loadPreviousPage({ ...getState() });
    } catch (error) {
      bugsnagError(error, { search: search() });
    }

    let updates = { status: 'Complete' };
    if (searchResults) {
      updates.searchResults = searchResults;
    }

    dispatch(ProductSearch._updateSearchState(search().makeCopy(updates)));

    return Promise.resolve();
  }

  /**
   * Updates the search with new Filter settings.
   * This does NOT run the search. However, any
   * changes made will apply the next time the search
   * is run.
   * @param {string} name - The unique search name
   * @param {Object} enabledFilters - The new enabled filters and values.
   */
  static updateFilters = ({ name, enabledFilters }) => {
    return ProductSearch._updateSearch({ name, enabledFilters });
  }

  /**
   * Updates the search with new sort settings.
   * This does NOT run the search. However, any
   * changes made will apply the next time the search
   * is run.
   * @param {string} name - The unique search name
   * @param {Object} enabledSort - The new enabled sort.
   */
  static updateSort = ({ name, enabledSort }) => {
    return ProductSearch._updateSearch({ name, enabledSort });
  }

  /**
   * Updates the search with a new product ID.
   * This does NOT run the search. However, any
   * changes made will apply the next time the search
   * is run.
   * @param {string} name - The unique search name
   * @param {Object} productId - The new enabled filters and values.
   */
  static updateProduct = ({ name, productId }) => {
    return ProductSearch._updateSearch({ name, productId });
  }

  /**
   * Updates the search with new settings.
   * Still does NOT run the search. Probably
   * don't run this directly, but make a method like the
   * ones above to filter out spurious changes.
   * @param {string} name - The unique search name
   * @param {Object} data - A collection of all other properties on the argument object.
   */
  static _updateSearch = ({ name, ...data }) => (dispatch, getState) => {
    const search = getState()[ProductSearch.reducerKey][name];
    if (!search) {
      bugsnagWarn('Attempted to update a non-existing search', { name, data });
      return;
    }
    const updated = search.makeCopy(data);
    dispatch(ProductSearch._updateSearchState(updated));
  }

  /** @private */
  static _updateSearchState = (updatedSearch) => dispatch => {
    dispatch({
      type: ProductSearch.SEARCH_UPDATED,
      payload: {
        name: updatedSearch.name(),
        search: updatedSearch
      }
    });
  }

  // ===================
  // SELECTORS / GETTERS
  // ===================
  static emptyArray = [];
  static emptyObject = {};

  /**
   * Sets a searchKey for Product Alternative Catalog search
   * @param {string} searchvalue - ex: 'blue chairs'
   * @return {string} searchKey -  ex: 'PACSearch-productId-bluechairs'
   */
  static buildPACSearchKey = ({ productId, searchValue }) => {
    const trimmedSearchValue = searchValue.replace(/\s+/g, '') || '';
    return `${ProductSearch.SEARCH_TYPE_PRODUCT_ALTERNATIVES_CATALOG}-${productId}-${trimmedSearchValue}`;
  }

  /**
  * Returns whether or not this is a Product Alt Catalog Search.
  * @param {string} searchKey
  * @returns {boolean} isFromPACSearch - Whether or not the search is PAC.
  */
  static isFromPACSearch = ({ searchKey }) => {
    return searchKey && searchKey.includes(ProductSearch.SEARCH_TYPE_PRODUCT_ALTERNATIVES_CATALOG);
  };

  /**
   * Returns whether or not the search is running.
   * @param {string} name - The unique search name
   * @param {Object} state - The redux state object
   * @returns {boolean} isRunning - Whether or not the search is currently running.
   */
  static isRunning = ({ name, state }) => {
    const search = state[ProductSearch.reducerKey][name];
    return search ? search.isRunning() : false;
  }

  /**
   * Returns whether or not a search with the given name exists
   * @param {string} name - The unique search name
   * @param {Object} state - The redux state object
   */
  static searchExists = ({ name, state }) => {
    const search = state[ProductSearch.reducerKey][name];
    return !!search;
  }

  /**
   * Returns whether or not the search has an available
   * next page of results not yet loaded.
   */
  static hasNextPage = ({ name, state }) => {
    const search = state[ProductSearch.reducerKey][name];
    return search ? search.hasNextPage() : false;
  }

  /**
   * Returns whether or not the search is loading the next page of results.
   * @param {string} name - The unique search name
   * @param {Object} state - The redux state object
   * @returns {boolean} isLoading - Whether or not the search is currently running.
   */
  static isLoadingNextPage = ({ name, state }) => {
    const search = state[ProductSearch.reducerKey][name];
    return search ? search.isLoadingNextPage() : false;
  }

  /**
   * Returns whether or not the last search execution
   * failed and, if so, the error message.
   * @param {string} name - The unique search name
   * @param {Object} state - The redux state object
   */
  static isFailed = ({ name, state }) => {
    const search = state[ProductSearch.reducerKey][name];
    const failed = search ? search.hasError() : false;
    const error = failed ? search.error() : null;
    return { failed, error };
  }

  /**
   * Returns the product results for the last executed search.
   * @param {string} name - The unique search name
   * @param {Object} state - The redux state object
   */
  static getSearchResultFor = ({ name, state }) => {
    const search = state[ProductSearch.reducerKey][name];
    return search ? search.products(state) : ProductSearch.emptyArray;
  }

  static getCurrentSearchResultFor = (name) => (dispatch, getState) => {
    return ProductSearch.getSearchResultFor({ name, state: getState() });
  }

  /**
   * Returns the search result instance for the last executed search.
   * @param {string} name - The unique search name
   * @param {Object} state - The redux state object
   */
  static getSearchResult = ({ name, state }) => {
    const search = state[ProductSearch.reducerKey][name];
    return search;
  }

  static getCurrentSearchResult = (name) => (dispatch, getState) => {
    return ProductSearch.getSearchResult({ name, state: getState() });
  };

  /**
   * Returns the enabled filters for a given search.
   * @param {string} name - The unique search name.
   * @param {Object} state - The redux state object
   */
  static getEnabledFiltersFor = ({ name, state }) => {
    const search = state[ProductSearch.reducerKey][name];
    return search ? search.enabledFilters() : ProductSearch.emptyObject;
  }

  static getCurrentEnabledFiltersFor = (name) => (dispatch, getState) => {
    return ProductSearch.getEnabledFiltersFor({ name, state: getState() });
  };

  /**
   * Returns the available filters for a given search.
   * @param {string} name - The unique search name.
   * @param {Object} state - The redux state object
   */
  static getAvailableFiltersFor = ({ name, state }) => {
    const search = state[ProductSearch.reducerKey][name];
    return search ? search.availableFilters({ state }) : ProductSearch.emptyArray;
  }

  /**
   * Returns the enabled sort for a given search.
   * @param {string} name - The unique search name.
   * @param {Object} state - The redux state object
   */
  static getEnabledSortFor = ({ name, state }) => {
    const search = state[ProductSearch.reducerKey][name];
    return search ? search.enabledSort() : null;
  }

  /**
   * Returns the available sorts for a given search.
   * @param {string} name - The unique search name.
   * @param {Object} state - The redux state object
   */
  static getAvailableSortsFor = ({ name, state }) => {
    const search = state[ProductSearch.reducerKey][name];
    return search ? search.availableSorts({ state }) : null;
  }

  /**
  * Returns all searchKeys.
  * @param {Object} state - The redux state object
  * @return {Object} allSearchKeys
  */
  static getAllSearchKeys = ({ state }) => {
    const allSearchKeys = state[ProductSearch.reducerKey];
    return allSearchKeys;
  }

  /**
  * Returns the current searchKeys.
  * @param {Function} dispatch - Method to update redux store
  * @param {Object} state - The redux state object
  * @return {Object} currentSearchKeys
  */
  static getCurrentSearchKeys = () => (dispatch, getState) => {
    return ProductSearch.getAllSearchKeys({ state: getState() });
  }

  /**
   * Returns the number of results for a given search.
   * @param {string} name - The unique search name.
   * @param {Object} state - The redux state object
   */
  static getNumberOfResultsFor = ({ name, state }) => {
    const search = state[ProductSearch.reducerKey][name];
    return search ? search.numberOfResults({ state }) : null;
  }

  /**
   * Returns the number of 2d results for a given search.
   * @param {string} name - The unique search name.
   * @param {Object} state - The redux state object
   */
  static getNumberOf2dResultsFor = ({ name, state }) => {
    const search = state[ProductSearch.reducerKey][name];

    return search && search.numberOf2dResults ? search.numberOf2dResults({ state }) : null;
  }
  /**
   * Returns the number of 3d results for a given search.
   * @param {string} name - The unique search name.
   * @param {Object} state - The redux state object
   */
  static getNumberOf3dResultsFor = ({ name, state }) => {
    const search = state[ProductSearch.reducerKey][name];
    return search && search.numberOf3dResults ? search.numberOf3dResults({ state }) : null;
  }

  /**
    * Returns the current searchKeys.
    * @param {Function} dispatch - Method to update redux store
    * @param {Object} state - The redux state object
    * @return {Object} currentSearchKeys
    */
  static getCurrentNumberOfResultsFor = (name) => (dispatch, getState) => {
    return ProductSearch.getNumberOfResultsFor({ name, state: getState() });
  }

  // =================
  // REDUCER / MEMBERS
  // =================
  static initialState = {} // mapping of search name/type to Search instance

  static reducerKey = 'ProductSearch';
  static reducer = (state = ProductSearch.initialState, action) => {
    switch (action.type) {
      case ProductSearch.SEARCH_UPDATED: {
        const { name, search } = action.payload;
        return {
          ...state,
          [name]: search
        };
      }

      default:
        return state;
    }
  }
}
